import {DiscountBenefitJSON, DiscountConditionJSON, DiscountJSON} from "../backend.service";
import {Cart} from "../cart.service";
import {DiscountBenefitType, DiscountConditionType} from "../constants";
import {Menu, MenuGroup, MenuItem} from "./menu";
import {getCurrentDate} from "../util";

export class DiscountCondition {
  type: DiscountConditionType;
  amount: string;
  order_method: string;
  payment_method: string;
  group_id: number;
  item_id: number;
  item: MenuItem;
  group: MenuGroup;
  from_hour: string;
  to_hour: string;
  count: number;

  constructor(init?: Partial<DiscountConditionJSON>, menu?: Menu) {
    Object.assign(this, init);
    if (menu) {
      if (this.item_id) {
        this.item = menu.items[this.item_id];
      }
      if (this.group_id) {
        this.group = menu.groups[this.group_id];
      }
    }
  }

  /**
   * parse hours (as in from_hour, to_hour) into an actual Date.
   * @param hourstring, like "12:00:00"
   * @param base base date, using today if not specified
   */
  parseHours(hourstring: string, base: Date = null): Date {
    const dt = getCurrentDate();
    if (base) {
      dt.setTime(base.getTime());
    }
    dt.setHours(parseInt(hourstring.substring(0, 2)));
    dt.setMinutes(parseInt(hourstring.substring(3, 5)));
    dt.setSeconds(parseInt(hourstring.substring(6, 8)));
    dt.setMilliseconds(0);
    return dt;
  }

  /**
   * Check if (and how often) the cart satisfies this condition.
   * @param cart
   */
  checkIfApplies(cart: Cart): number {
    let count = 0;
    switch (this.type) {
      case DiscountConditionType.PaymentMethod:
        return (cart.payment == this.payment_method) ? this.getItemCount(cart) : 0;
      case DiscountConditionType.OrderMethod:
        return (cart.method == this.order_method) ? this.getItemCount(cart) : 0;
      case DiscountConditionType.ItemFromGroup:
        count = cart.positions.reduce(
          (acc, pos) => (pos.item && pos.item.isInGroup(this.group_id)) ? acc + pos.count : acc, 0);
        return Math.floor(count / this.count);
      case DiscountConditionType.Item:
        count = cart.positions.reduce(
          (acc, pos) => (pos.item && pos.item.id == this.item.id) ? acc + pos.count : acc, 0);
        return Math.floor(count / this.count);
      case DiscountConditionType.Time:
        let nextPossibleTime = new Date();
        if (cart.desired_time == null || cart.desired_time == 0 /* asap */) {
          // calculate earliest delivery/takeout time
          nextPossibleTime = cart.getEarliestDeliveryTime();
        } else {
          // use actual desired time
          nextPossibleTime.setTime(cart.desired_time*1000);
        }
        // nextPossibleTime can be today or tomorrow, use it as base
        // to determine limits
        let lower = this.parseHours(this.from_hour, nextPossibleTime),
            upper = this.parseHours(this.to_hour, nextPossibleTime);

        return (lower <= nextPossibleTime && upper >= nextPossibleTime) ? 1 : 0;

      case DiscountConditionType.Value:
        return Math.floor(cart.getNetTotal() / parseFloat(this.amount));

      case DiscountConditionType.ValueFromGroup:
        return Math.floor(cart.getNetTotal(this.group_id) / parseFloat(this.amount));
    }
  }

  /**
   * return the number of items in cart (except free and discounts)
   * @param cart
   */
  getItemCount(cart: Cart): number {
    return cart.positions.reduce((count, pos) => (pos.item)?count+pos.count:count, 0);
  }

}

export class DiscountBenefit {
  id: number;
  discount: Discount;
  type: DiscountBenefitType;
  amount: string;
  percentage: string;
  item_id: number;
  group_id: number;
  item: MenuItem;
  group: MenuGroup;
  count: number;

  constructor(init: Partial<DiscountBenefitJSON>, discount: Discount, menu?: Menu) {
    Object.assign(this, init);
    this.discount = discount;
    if (menu) {
      if (this.item_id) {
        this.item = menu.items[this.item_id];
      }
      if (this.group_id) {
        this.group = menu.groups[this.group_id];
      }
    }
  }

  getAmount(cart: Cart, currentTotal: number): number {
    switch (this.type) {
      case DiscountBenefitType.Fixed:
        let amount = parseFloat(this.amount);
        // amount must be limited to currentTotal, so we don't get a negative total
        return Math.min(amount, currentTotal);
      case DiscountBenefitType.Percentage:
        return parseFloat(this.percentage) * cart.getNetTotal();
      case DiscountBenefitType.PercentageFromGroup:
        return parseFloat(this.percentage) * cart.getNetTotal(this.group_id);
      default:
        return null;
    }
  }

  /**
   * returns true if this benefit needs a selection (e.g. type is ItemFromGroup or
   * type is Item and the item has variants)
   */
  needsSelection(): boolean {
    return (this.type == DiscountBenefitType.FreeItemFromGroup ||
      (this.type == DiscountBenefitType.FreeItem && this.item && this.item.hasVariants()));
  }

  /**
   * returns true if this benefit has a monetary value (amount), e.g. type is
   * one of Fixed, Percentage, PercentageFromGroup
   */
  hasAmount(): boolean {
    return (this.type == DiscountBenefitType.Fixed ||
      this.type == DiscountBenefitType.Percentage ||
      this.type == DiscountBenefitType.PercentageFromGroup)
  }

  /**
   * get a flat list of possible items and variants (only used in host-order)
   */
  getPossibleSelections(): Array<{key, item, variant, title}> {
    let items, choices;
    if (this.item) {
      items = [this.item];
    } else {
      items = this.group.item_list().reduce((acc, section) => {
          return acc.concat(section.items);
        }, []);
    }
    choices = items.reduce((acc, item) => {
      if (item.hasVariants()) {
        item.variants.forEach((v) => {
          acc.push({key: `${item.id}-${v.id}`, item: item, variant: v, title: item.name + ' (' + v.name + ')'});
        })
      } else {
        acc.push({key: `${item.id}-0`, item: item, variant: null, title: item.name});
      }
      return acc;
    }, []);
    return choices;
  }
}

export class Discount {
  id: number;
  type: string;
  title: string;
  text: string;
  code: string;
  promote_text: string;
  app_only: boolean;
  multiple: boolean;
  conditions: Array<DiscountCondition>;
  benefits: Array<DiscountBenefit>;

  constructor(init?: Partial<DiscountJSON>, menu?: Menu) {
    Object.assign(this, init);
    this.conditions = [];
    for (let i = 0; i < init.conditions.length; i++) {
      this.conditions.push(new DiscountCondition(init.conditions[i], menu));
    }
    this.benefits = [];
    for (let i = 0; i < init.benefits.length; i++) {
      this.benefits.push(new DiscountBenefit(init.benefits[i], this, menu));
    }
  }

  /**
   * Check if (and how often) this discount applies to the cart.
   * @param cart
   */
  checkIfApplies(cart: Cart): number {
    if (cart.positions.length == 0) {
      // empty cart gets no discounts
      return 0;
    }
    if (this.conditions.length == 0) {
      return 1; // no condition = always applies
    } else {
      const count = Math.min(...this.conditions.map(cond => cond.checkIfApplies(cart)));
      // return the minimum count
      return (this.multiple) ? count : Math.min(count, 1);
    }
  }

  /**
   * Check if this discount has at least one condition which requires
   * a `value` of the given `type`.
   *
   * Ex. `appliesTo(DiscountConditionType.OrderMethod, OrderMethod.Takeout)`
   *     returns True, if this discount has at least one condition which
   *     requires `OrderMethod` to be `Takeout`.
   *
   * Used to announce discounts in order method and payment selection. Do
   * not confuse with `checkIfApplies()`, which checks if a Discount actually
   * applies to a given `Cart`.
   *
   * @param type
   * @param value
   */
  appliesTo(type: DiscountConditionType, value: any): boolean {
    let attr = null;
    switch (type) {
      case DiscountConditionType.OrderMethod:
        attr = 'order_method';
        break;
      case DiscountConditionType.PaymentMethod:
        attr = 'payment_method';
        break;
      case DiscountConditionType.Item:
        attr = 'item';
        break;
      case DiscountConditionType.ItemFromGroup:
        attr = 'group';
        break;
      default:
        return false;
    }

    return this.conditions.some(cond =>
      (cond.type == type && cond[attr] == value));
  }

  hasConditionType(type: DiscountConditionType): boolean {
    return this.conditions.some(cond => cond.type == type);
  }

  needsSelection(): boolean {
    return this.benefits.some(benefit => benefit.needsSelection());
  }

  getAmount(cart: Cart, currentTotal: number): number {
    return this.benefits.reduce(
      (total, benefit) => total + benefit.getAmount(cart, currentTotal),
      0);
  }

  /**
   * return whether the discount includes a free item (either item or choice)
   */
  includesFreeItem(): boolean {
     return this.benefits.some(b=>(b.type==DiscountBenefitType.FreeItem || b.type == DiscountBenefitType.FreeItemFromGroup))
  }
}
